/**
 * Single source of truth for the `FB.login` options that launch WhatsApp
 * Embedded Signup.
 *
 * Two surfaces launch the flow (the shared connect state machine and the
 * legacy Dashboard path). Before INT-1257 each carried its own copy of this
 * options block, so a version migration could silently update one and leave
 * the other on the old flow. Every launcher MUST build its options here.
 *
 * Version notes (INT-1254 — Embedded Signup v4 migration):
 * - Which flow experience the customer sees (v2/v4 screens) is decided by the
 *   Facebook Login for Business configuration behind `config_id`
 *   (`VITE_WAB_CONFIG_ID`, injected per environment via Doppler) — not by
 *   anything in this object. Swapping the env var is the whole activation.
 * - `sessionInfoVersion: '3'` is REQUIRED while the active configuration is
 *   pre-v4: it is what makes Meta post the `WA_EMBEDDED_SIGNUP` session info
 *   that CoEx onboarding and the INT-1216 telemetry depend on. On a v4
 *   configuration Meta always sends session info and ignores this field, so
 *   keeping it is harmless. Remove it only after every environment's
 *   `config_id` points to a v4 configuration.
 * - `featureType` keeps WhatsApp Business *app* customers onboardable (still
 *   supported on v4 via `feature_type`). Meta picks the path from it on the
 *   same `config_id`: with it the customer gets Coexistence, without it the
 *   default Cloud API flow for a dedicated number. Only the `cloud_api` path
 *   omits it; the default is `coexistence`.
 */
import { VITE_META_FEATURE, VITE_WAB_CONFIG_ID } from 'src/constants/envs';

/** The Embedded Signup flow the customer chose to connect with. */
export type tEmbeddedSignupPath = 'coexistence' | 'cloud_api';

/** Options for {@link buildEmbeddedSignupLoginOptions}. */
export type tEmbeddedSignupLoginParameters = {
  path?: tEmbeddedSignupPath;
};

export type tEmbeddedSignupLoginOptions = {
  config_id: string;
  response_type: 'code';
  override_default_response_type: true;
  extras: {
    setup: Record<string, never>;
    featureType?: string;
    sessionInfoVersion: '3';
  };
};

/**
 * Builds the `FB.login` options for the chosen Embedded Signup path.
 *
 * @param {tEmbeddedSignupLoginParameters} [parameters] - Builder options.
 * @param {tEmbeddedSignupPath} [parameters.path='coexistence'] - The flow Meta opens.
 * @returns {tEmbeddedSignupLoginOptions} A fresh options object per call.
 */
export function buildEmbeddedSignupLoginOptions({
  path = 'coexistence',
}: tEmbeddedSignupLoginParameters = {}): tEmbeddedSignupLoginOptions {
  return {
    config_id: VITE_WAB_CONFIG_ID,
    response_type: 'code',
    override_default_response_type: true,
    extras: {
      setup: {},
      ...(path === 'coexistence' && { featureType: VITE_META_FEATURE }),
      sessionInfoVersion: '3',
    },
  };
}

// The desktop width the SDK gives the Embedded Signup popup. On mobile the SDK
// opens a full tab with no width, which is how mobile popups stay untouched.
const META_POPUP_WIDTH = 600;

/**
 * Rewrites the `window.open` features of the Meta popup so it opens against the
 * left edge of the browser window instead of centered over it. There it covers
 * the wizard's main panel and leaves the aside with the steps visible.
 *
 * @param {string | undefined} features - Comma-separated `key=value` features.
 * @param {Pick<Window, 'screenX'>} browserWindow - The window the popup is pinned to.
 * @returns {string | undefined} The features with `left` pinned, or the input
 *   unchanged when it does not carry `width=600`.
 */
export function pinPopupFeaturesToLeftEdge(
  features: string | undefined,
  browserWindow: Pick<Window, 'screenX'>,
): string | undefined {
  if (!features) return features;
  const entries = features.split(',');
  if (!entries.some((entry) => entry.trim() === `width=${META_POPUP_WIDTH}`)) {
    return features;
  }

  const left = browserWindow.screenX;
  // Browsers read `left=NaN` as 0, the left edge of the screen rather than of
  // the browser window.
  if (!Number.isFinite(left)) return features;

  const leftEntry = `left=${left}`;
  const hasLeft = entries.some(
    (entry) => entry.split('=')[0].trim() === 'left',
  );
  const pinnedEntries = hasLeft
    ? entries.map((entry) =>
        entry.split('=')[0].trim() === 'left' ? leftEntry : entry,
      )
    : [...entries, leftEntry];
  return pinnedEntries.join(',');
}

/**
 * Runs `run` with `window.open` replaced so the popup it opens is pinned to the
 * left edge of the browser window. `FB.login` takes no position option and the
 * popup cannot be moved once it loads Meta's origin, so its features are the
 * only place to set it.
 *
 * The replacement lives only while `run` executes and the original is restored
 * even when `run` throws. When pinning fails, or `window.open` cannot be
 * replaced, the popup opens with the features the caller passed.
 *
 * @template T
 * @param {() => T} run - The code that opens the popup synchronously.
 * @returns {T} What `run` returns.
 */
export function withPopupPinnedToLeftEdge<T>(run: () => T): T {
  const originalWindowOpen = window.open;

  try {
    window.open = (...openArguments: Parameters<typeof window.open>) => {
      const [url, target, features] = openArguments;
      let openFeatures: string | undefined;
      try {
        openFeatures = pinPopupFeaturesToLeftEdge(features, window);
      } catch {
        openFeatures = features;
      }
      return originalWindowOpen.call(window, url, target, openFeatures);
    };
  } catch {
    // A read-only `window.open` must not cost the connection: open centered.
    return run();
  }

  try {
    return run();
  } finally {
    window.open = originalWindowOpen;
  }
}
